/* eslint-disable */
import React, { useMemo, useState } from 'react';
import { CTitle, TTitle } from '../../components/Widget';
import { useParams } from 'react-router-dom';
import { useUpdateComponentState } from '../../../hooks';
import ImgPoint from '../../../../../../assets/2025/arrow.gif';
import arrow from '../../../../../../assets/2025/arrow.gif';
import phishing from '../../../../../../assets/finance/topic900/phishing.png';
import TamperingImg1 from '../../../../../../assets/finance/topic900/tampering_img_1.png';
import TamperingImg2 from '../../../../../../assets/finance/topic900/tampering_img_2.png';
import section3Icon1 from '../../../../../../assets/finance/topic900/section3-icon1.png';
import section3Icon2 from '../../../../../../assets/finance/topic900/section3-icon2.png';
import Quiz from '../../components/Quiz';

interface ISection2Props {
  settings: any;
}

const Section3: React.FunctionComponent<ISection2Props> = ({ settings }) => {
  const param = useParams();
  const p_course = param.course || '';
  const p_topic = param['*'] || 'welcome';
  const paragraphsSettings = useMemo(() => settings?.paragraphs[3], [settings]);
  const updateComponentState = useUpdateComponentState();

  const triggerUpdate = React.useCallback(
    (key: string) => updateComponentState(p_course, p_topic, '4', key),
    [updateComponentState, p_course, p_topic]
  );

  const [tamperingVisibleCount, setTamperingVisibleCount] = useState(1);
  const [tamperingAnimationStarted, setTamperingAnimationStarted] = useState(false);
  const [accountVisibleCount, setAccountVisibleCount] = useState(1);
  const [accountAnimationStarted, setAccountAnimationStarted] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const hasUpdatedKeyPointsRef = React.useRef(false);

  const quiz = paragraphsSettings?.quiz;

  const iconMap: any = {
    1: section3Icon1,
    2: section3Icon2,
  };

  const handleTamperingArrowClick = React.useCallback(() => {
    if (tamperingAnimationStarted) return;
    setTamperingAnimationStarted(true);
    setTamperingVisibleCount(2);
    triggerUpdate('finance-topic900-section3-2');
  }, [tamperingAnimationStarted, triggerUpdate]);

  const handleAccountArrowClick = React.useCallback(() => {
    if (accountAnimationStarted) return;
    setAccountAnimationStarted(true);
    setAccountVisibleCount(2);
    triggerUpdate('finance-topic900-section3-4');
  }, [accountAnimationStarted, triggerUpdate]);

  React.useEffect(() => {
    if (!tamperingAnimationStarted || tamperingVisibleCount >= paragraphsSettings.risks?.length) return;
    const timer = setTimeout(() => setTamperingVisibleCount((v) => v + 1), 600);
    return () => clearTimeout(timer);
  }, [tamperingVisibleCount, tamperingAnimationStarted, paragraphsSettings.risks?.length]);

  React.useEffect(() => {
    if (!accountAnimationStarted || accountVisibleCount >= paragraphsSettings.risks?.length) return;
    const timer = setTimeout(() => setAccountVisibleCount((v) => v + 1), 600);
    return () => clearTimeout(timer);
  }, [accountVisibleCount, accountAnimationStarted, paragraphsSettings.risks?.length]);

  const handleItemClick = React.useCallback((id: number) => {
    setSelectedItemIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  React.useEffect(() => {
    if (selectedItemIds.length < 2) {
      hasUpdatedKeyPointsRef.current = false;
      return;
    }
    if (hasUpdatedKeyPointsRef.current) return;
    hasUpdatedKeyPointsRef.current = true;
    triggerUpdate('finance-topic900-section3-5');
  }, [selectedItemIds.length, triggerUpdate]);

  const selectedIdSet = useMemo(() => new Set(selectedItemIds), [selectedItemIds]);

  // ---------- factor animation (lines 95–130) ----------
  const [factorPlay, setFactorPlay] = useState(1);
  const [middleFlowPlay, setMiddleFlowPlay] = useState(1);
  const factorListLength = paragraphsSettings?.l1RightList?.length ?? 5;
  const middleFlowListLength = paragraphsSettings?.l2RightList?.length ?? 5;
  const hasUpdatedFactorRef = React.useRef(false);
  const hasUpdatedMiddleFlowRef = React.useRef(false);

  const startFactorPlay = React.useCallback(() => {
    if (factorPlay > factorListLength) return;
    setFactorPlay((prev) => {
      if (prev > factorListLength) return prev;
      return prev + 1;
    });
  }, [factorPlay, factorListLength]);

  React.useEffect(() => {
    if (factorPlay > 1 && factorPlay < factorListLength) {
      const timer = setTimeout(() => setFactorPlay((p) => p + 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [factorPlay, factorListLength]);

  React.useEffect(() => {
    if (factorPlay < factorListLength || factorPlay <= 1) return;
    if (hasUpdatedFactorRef.current) return;
    hasUpdatedFactorRef.current = true;
    triggerUpdate('finance-topic900-section3-1');
  }, [factorPlay, factorListLength]);

  const startMiddleFlowPlay = React.useCallback(() => {
    if (middleFlowPlay >= middleFlowListLength) return;
    setMiddleFlowPlay((prev) => {
      if (prev >= middleFlowListLength) return prev;
      return prev + 1;
    });
  }, [middleFlowPlay, middleFlowListLength]);

  React.useEffect(() => {
    if (middleFlowPlay > 1 && middleFlowPlay < middleFlowListLength) {
      const timer = setTimeout(() => setMiddleFlowPlay((p) => p + 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [middleFlowPlay, middleFlowListLength]);

  React.useEffect(() => {
    if (middleFlowPlay < middleFlowListLength || middleFlowPlay <= 1) return;
    if (hasUpdatedMiddleFlowRef.current) return;
    hasUpdatedMiddleFlowRef.current = true;
    triggerUpdate('finance-topic900-section3-3');
  }, [middleFlowPlay, middleFlowListLength]);

  // ---------- reusable RiskList component ----------
  interface RiskListProps {
    risks: any[];
    visibleCount: number;
    animationStarted: boolean;
    onClick?: () => void;
    renderItem: (risk: any, index: number) => React.ReactNode;
  }

  const RiskList: React.FC<RiskListProps> = ({
    risks,
    visibleCount,
    animationStarted,
    onClick,
    renderItem,
  }) => (
   <>
     <div
       className="left-list"
       onClick={onClick}
       style={{ cursor: onClick && !animationStarted ? 'pointer' : 'default' }}
     >
       {risks?.map((r, i) => (
         <div
           key={i}
           className="risk-item"
           data-show={i < visibleCount}
           style={{ cursor: onClick && !animationStarted ? 'pointer' : 'default' }}
         >
           {renderItem(r, i)}
         </div>
       ))}
     </div>
     <div
       className="m-left-list"
       onClick={onClick}
       style={{ cursor: onClick && !animationStarted ? 'pointer' : 'default' }}
     >
       {risks?.map((r, i) => (
         <div
           key={i}
           className="risk-item"
           data-show={i < visibleCount}
           style={{ cursor: onClick && !animationStarted ? 'pointer' : 'default' }}
         >
           {renderItem(r, i)}
         </div>
       ))}
     </div>
   </>
  );

  // ---------- section 1: tampering (lines 95–130) ----------
  const TamperingSection = () => {
    return (
      <div className="tampering">
        <TTitle title={paragraphsSettings?.tamperingTitle} />
        <div className="content" id="finance-topic900-section3-2">
          {!tamperingAnimationStarted && <img className="arrow-img" src={arrow} alt="" />}
          <img className="tampering_img_1" src={TamperingImg1} alt=""/>
          {RiskList({
            risks: paragraphsSettings?.tamperingList,
            visibleCount: tamperingVisibleCount,
            animationStarted: tamperingAnimationStarted,
            onClick: handleTamperingArrowClick,
            renderItem: (r) => (
              <>
                <div className="risk-title">
                  <span>{r.title}</span>
                </div>
                <div className="risk-desc">
                  <div className="risk-desc-box">
                    {r.desc}
                  </div>
                </div>
              </>
            ),
          })}
          <img className="tampering_img_2" src={TamperingImg2} alt=""/>
        </div>
      </div>
    );
  };

  // ---------- section 2: account-management (lines 131–172) ----------
  const AccountManagementSection = () => {
    return (
      <div className="account-management">
          <CTitle title={paragraphsSettings?.risksTitle} />
        <div className="content" id="finance-topic900-section3-4">
          {!accountAnimationStarted && <img className="arrow-img" src={arrow} alt="" />}
          {RiskList({
            risks: paragraphsSettings.risks,
            visibleCount: accountVisibleCount,
            animationStarted: accountAnimationStarted,
            onClick: handleAccountArrowClick,
            renderItem: (r) => (
              <>
                <div className="risk-title">{r.title}</div>
                <div className="risk-desc">{r.desc}</div>
              </>
            ),
          })}
          <div className="right-pic">
            <div className="risks-title">{paragraphsSettings?.risksDesc}</div>
            <img src={phishing} alt="" />
          </div>
        </div>
      </div>
    );
  };

  const KeyPointsSection = () => {
    return (
      <div className="cybersecurity-key-points" id="finance-topic900-section3-5">
        <div className="cybersecurity-key-points-tip">{paragraphsSettings?.tips}</div>
        <div className="right-container">
          {paragraphsSettings?.listArr?.map((item: any) => (
            (() => {
              const isActive = selectedIdSet.has(item.id);
              return (
                <div
                  className={`cybersecurity-key-points-item ${isActive ? 'active' : ''}`}
                  key={item.id}
                  onClick={() => handleItemClick(item.id)}
                >
                  <div className="item-left">
                    <img src={iconMap[item.id] || iconMap[((item.id - 1) % 2) + 1]} alt="" />
                    <div
                      className="item-left-text"
                      dangerouslySetInnerHTML={{
                        __html: item?.title,
                      }}
                    ></div>
                    {!isActive && <img src={arrow} alt="" />}
                  </div>
                  {isActive && (
                    <div className="item-right">
                      <p>{item.phenomenon}</p>
                    </div>
                  )}
                </div>
              );
            })()
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="topic-section-3">
      <CTitle title={paragraphsSettings.title} />
      <div
        className="factor-content"
        id="finance-topic900-section3-1"
        onClick={startFactorPlay}
      >
        {factorPlay <= 1 && <img src={ImgPoint} alt="" className="img-point" />}
        <div className="factor-tips">{paragraphsSettings?.l1RightListDesc}</div>
        {paragraphsSettings?.l1RightList?.map((item: any, i: number) => (
          <div
            className={`factor-item factor-item-${i + 1} ${
              factorPlay > i ? 'box-fade-right-in' : ''
            }`}
            key={i}
          >
            <div>
              {item?.text}
            </div>
          </div>
        ))}
      </div>
      {TamperingSection()}
      <div
        className="middle-flow-content"
        id="finance-topic900-section3-3"
        onClick={startMiddleFlowPlay}
      >
        {middleFlowPlay <= 1 && <img src={ImgPoint} alt="" className="middle-flow-point" />}
        <div className="middle-flow-bubble-wrap">
          {paragraphsSettings?.l2RightListDesc}
        </div>
        <div className="middle-flow-list">
          {paragraphsSettings?.l2RightList?.map((item: any, i: number) => (
            <div
              className={`middle-flow-item middle-flow-item-${i + 1} ${
                middleFlowPlay > i ? 'box-fade-right-in' : ''
              }`}
              key={i}
            >
              <div className="middle-flow-item-index">{`0${i + 1}`}</div>
              <div className="middle-flow-item-text">
                {item?.text}
              </div>
            </div>
          ))}
        </div>
      </div>
      {AccountManagementSection()}
      <CTitle title={paragraphsSettings?.tipsTitle} />
      {KeyPointsSection()}
      <div
        id="finance-topic900-section3-question"
        className="topic-quiz"
      >
        <Quiz
          title={paragraphsSettings?.questionTitle}
          description={quiz?.description}
          question={quiz?.question}
          options={quiz?.options}
          onCompleted={() => {
            updateComponentState(
              p_course,
              p_topic,
              '4',
              'finance-topic900-section3-question'
            );
          }}
        />
      </div>
    </div>
  );
};

export default Section3;
